import type { Store } from 'redux';
import type { ParsedUrlQuery } from 'querystring';

import { wrapper } from '@/state/store';
import { actions as filterActions } from '@/state/filterActions';
import { resetFetchState, setSearchResults, setSearchResultsError } from '@/ducks/searchResults/actions';
import { actions as mapActions } from '@/state/mapActions';
import { browserQueryParamsToResultPageFilters, filtersToResultPageBrowserQueryParams } from '@/utils/url/resultPageQueryParams';
import { getHotels } from '@/utils/fetch/kayak/kayakFetcher';
import { UNLEASH_SESSION_COOKIE } from '@/utils/cookies';
import { queryParamToBoolean } from '@/utils/url/queryParamConverters';
import { buildCommonProps, sanitizeCommonProps } from '@/server/middleware';
import { type CheckInCheckOutErrorTexts, validateCheckInCheckOut } from '@/utils/dateValidationErrors';
import { queryParamToString } from 'shared/util/query';

import type {
    AccommodationState,
    ApiResponse,
    FavoriteHotels,
    FetchHeaders,
    Filters,
    NextPageRequest,
    NextPageResponse,
    SearchConfig,
    SearchResultsState,
} from '@/interfaces';
import { ErrorPage } from '@/scenes/errorPage/ErrorPage';
import SearchResultsPage from '@/scenes/searchResultsPage/SearchResultsPage';
import { getKayakPlaceId } from '@/server/datafeed/kayakDestinationId';

export interface SearchResultsPageProps {
    filterErrors: readonly string[];
    statusCode: number;
    favoriteHotels?: FavoriteHotels;
}

export const createHeaders = (req: NextPageRequest, res: NextPageResponse): FetchHeaders => {
    const headers = {
        'user-agent': req.headers['user-agent'],
        'client-ip': req.ip,
    };
    if (req.cookies[UNLEASH_SESSION_COOKIE] || res.locals?.unleashId) {
        return {
            ...headers,
            cookie: `${UNLEASH_SESSION_COOKIE}=${req.cookies[UNLEASH_SESSION_COOKIE] ?? res.locals.unleashId}`,
        };
    }
    return headers;
};

const validationErrorTexts: CheckInCheckOutErrorTexts = {
    missingCheckInDate: '',
    missingCheckOutDate: '',
    invalidCheckInDate: 'Velge en dato for innsjekk som er i dag eller de neste 365 dager',
    invalidCheckOutDate: 'Velge en dato for utsjekk som er etter dato for innsjekk',
    tooManyDaysBetweenDates: 'Velge opphold på mindre enn 30 dager',
};

const emptyResponse: ApiResponse<Partial<SearchResultsState>> = { status: 200, data: { isComplete: true }, url: '' };

const checkForFilterErrors = (filters: Partial<Filters>): readonly string[] =>
    validateCheckInCheckOut(validationErrorTexts, filters.checkInDate, filters.checkOutDate);

const emptyDestination = (filters: Filters): boolean =>
    !filters.destination && (!filters.hotelIds || filters.hotelIds.length === 0) && !filters.mapBounds;

export const fetchDataAndUpdateResponse = async (
    store: Store<AccommodationState>,
    { req, res, query }: { req: NextPageRequest; res: NextPageResponse; query: ParsedUrlQuery },
    createSearchConfig: (filters: Filters) => SearchConfig,
    adjustFilters: (filters: Partial<Filters>) => Partial<Filters> = (filters) => filters,
): Promise<SearchResultsPageProps> => {
    const filters: Partial<Filters> = adjustFilters(browserQueryParamsToResultPageFilters(query));
    store.dispatch(filterActions.setFilters(filters));
    if (!filters.checkInDate && !filters.checkOutDate) {
        store.dispatch(filterActions.changeSort({ value: 'rating', direction: 'descending' }));
    }
    const { status, data } = emptyDestination(store.getState().filters)
        ? emptyResponse
        : await getHotels(store.getState().filters, createHeaders(req, res));

    if (status === 200) {
        resetFetchState(store.dispatch, store.getState().filters, true);
        const searchConfig = createSearchConfig(store.getState().filters);
        store.dispatch(
            setSearchResults({
                data: { ...data, searchConfig },
                queryParams: filtersToResultPageBrowserQueryParams(store.getState().filters, false),
            }),
        );
        store.dispatch(mapActions.setMapState({ open: queryParamToBoolean(query.mapOpen) }));
    } else {
        store.dispatch(setSearchResultsError());
        res.statusCode = status;
    }

    return {
        filterErrors: checkForFilterErrors(filters),
        statusCode: status,
    };
};

const Destination = (props: SearchResultsPageProps) => {
    wrapper.useHydration(props);
    const { filterErrors, statusCode } = props;
    if (statusCode !== 200) {
        return <ErrorPage statusCode={statusCode} />;
    }

    return <SearchResultsPage filterErrors={filterErrors} />;
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ req, res, query }) => {
    if (!req) {
        return { props: { filterErrors: [], statusCode: 200 } };
    }

    const commonProps = await buildCommonProps(req as NextPageRequest, res, query);
    const unleash = { ...commonProps.unleash };
    const destinationId = queryParamToString(query.destination[0]);

    // Checks if destinationId is not a number...
    if (!/^\d+$/.test(destinationId) && unleash.newAutocomplete) {
        return getKayakPlaceId(destinationId).then((destination) => {
            if (destination) {
                return {
                    redirect: {
                        destination: `/resultat/${destination.newId}/${destination.placename}`,
                        permanent: false,
                    },
                };
            }
            return {
                redirect: {
                    destination: '/error',
                    permanent: false,
                },
            };
        });
    }

    const searchResultsPageProps = await fetchDataAndUpdateResponse(store, { req, res, query }, (filters: Filters) => {
        return {
            nextPath: 'resultat/[...destination]',
            urlPath: `resultat/${filters.destination}${query.destination.length > 1 ? `/${query.destination[1]}` : ''}`,
            fixed: false,
        };
    });

    return {
        props: {
            ...searchResultsPageProps,
            ...sanitizeCommonProps(commonProps),
        },
    };
});

export default Destination;
